import { useMemo } from 'react';
import type { MerchantSlot } from '../../services/merchantSlots.api';
import { slotStyle } from '../../services/merchantSlots.api';

// Timeline runs from 07:00 to 07:00 next day. Slot work sits between 13:00 and
// 02:00, so a 07:00 origin keeps the whole grid contiguous — no wrap-around.
export const DAY_START_HOUR = 7;
export const DAY_START_OFFSET = DAY_START_HOUR * 60; // 420 minutes

/** Convert absolute minute-of-day to timeline-relative position */
export function toTimelinePos(absMin: number): number {
  return ((absMin - DAY_START_OFFSET) + 1440) % 1440;
}

export interface TimelineBar {
  merchantName: string;
  merchantId: number;
  isDedicated: boolean;
  dedicatedDriverName: string | null;
  slotCode: string;
  slotLabel: string | null;
  startMin: number; // timeline-relative position (0 = 07:00)
  endMin: number;   // timeline-relative position
  durationMin: number;
  color: string;
  label: string;
  // Keep absolute times for tooltip display
  absStartMin: number;
  absEndMin: number;
}

export interface MerchantRow {
  merchantName: string;
  merchantId: number;
  isDedicated: boolean;
  slotCode: string;
  bars: TimelineBar[];
}

export interface TimelineData {
  rows: MerchantRow[];
  densityBuckets: number[];
  peakOverlap: number;
  peakBucketIndex: number;
  totalMerchants: number;
  busiestSlot: string;
  /** Merchants per slot code, in S1..S5 order. */
  slotCounts: Array<{ code: string; label: string | null; count: number; window: string }>;
  dedicatedCount: number;
  unsetCount: number;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Build the timeline from each merchant's CORE slot window (S1..S5), not from the
 * retired free-form fleet windows. Every merchant on a given slot therefore draws
 * an identical bar — which is the point: the density curve now shows real booked
 * load per slot rather than 206 hand-typed ranges.
 *
 * Merchants with no slot set are excluded from bars (they have no window to draw)
 * but counted in `unsetCount` so they are not silently lost.
 */
export function useTimelineData(merchants: MerchantSlot[]): TimelineData {
  return useMemo(() => {
    const BUCKETS = 48; // 30-min buckets; bucket 0 = 07:00, bucket 47 = 06:30
    const densityBuckets = new Array(BUCKETS).fill(0);
    const rows: MerchantRow[] = [];
    const perSlot = new Map<string, { label: string | null; count: number; window: string }>();
    let dedicatedCount = 0;
    let unsetCount = 0;

    for (const m of merchants) {
      if (m.requires_dedicated_driver) dedicatedCount++;

      if (!m.slot_code || !m.slot_window) {
        unsetCount++;
        continue;
      }

      const windowStr = `${m.slot_window.from}–${m.slot_window.to}`;
      const agg = perSlot.get(m.slot_code) ?? { label: m.slot_label, count: 0, window: windowStr };
      agg.count++;
      perSlot.set(m.slot_code, agg);

      const absStart = timeToMinutes(m.slot_window.from);
      let absEnd = timeToMinutes(m.slot_window.to);
      // S4 ends at "00:00" (raw 1440) and S5 runs 00:00–02:00 — both mean the NEXT
      // calendar day. Without this an S4 bar would have negative width and an S5
      // bar would land at 05:00 on the wrong side of the 07:00 origin.
      if (absEnd <= absStart) absEnd += 1440;
      const durationMin = absEnd - absStart;

      const tlStart = toTimelinePos(absStart);
      const style = slotStyle(m.slot_code);

      const bar: TimelineBar = {
        merchantName: m.merchant_name,
        merchantId: m.merchant_id,
        isDedicated: m.requires_dedicated_driver,
        dedicatedDriverName: m.dedicated_driver_name,
        slotCode: m.slot_code,
        slotLabel: m.slot_label,
        startMin: tlStart,
        endMin: tlStart + durationMin,
        durationMin,
        color: style.hex,
        label: m.slot_code,
        absStartMin: absStart,
        absEndMin: absEnd % 1440,
      };

      rows.push({
        merchantName: m.merchant_name,
        merchantId: m.merchant_id,
        isDedicated: m.requires_dedicated_driver,
        slotCode: m.slot_code,
        bars: [bar],
      });

      for (let b = Math.floor(tlStart / 30); b < Math.ceil((tlStart + durationMin) / 30) && b < BUCKETS; b++) {
        densityBuckets[b]++;
      }
    }

    rows.sort((a, b) => {
      const aStart = a.bars[0]?.startMin ?? 1440;
      const bStart = b.bars[0]?.startMin ?? 1440;
      return aStart - bStart || a.merchantName.localeCompare(b.merchantName);
    });

    const peakOverlap = Math.max(...densityBuckets, 0);
    const peakBucketIndex = densityBuckets.indexOf(peakOverlap);

    const slotCounts = ['S1', 'S2', 'S3', 'S4', 'S5']
      .filter((c) => perSlot.has(c))
      .map((c) => ({ code: c, ...perSlot.get(c)! }));

    const busiest = slotCounts.reduce<{ code: string; count: number } | null>(
      (best, s) => (!best || s.count > best.count ? { code: s.code, count: s.count } : best),
      null
    );

    return {
      rows,
      densityBuckets,
      peakOverlap,
      peakBucketIndex,
      totalMerchants: rows.length,
      busiestSlot: busiest ? `${busiest.code} (${busiest.count})` : '—',
      slotCounts,
      dedicatedCount,
      unsetCount,
    };
  }, [merchants]);
}
